import { useState, useCallback } from 'react';
import { CSVImporter, type ImportResult } from '@importcsv/react';
import { toast } from 'sonner';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';
import {
  feedbackImportColumns,
  feedbackImportSchema,
  getClientNameFromRow,
  type FeedbackImportRow,
} from '@/lib/import-schemas';
import { importCsvTheme } from '@/lib/import-theme';

interface ImportFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ImportFeedbackDialog({
  open,
  onOpenChange,
  onSuccess,
}: ImportFeedbackDialogProps) {
  const { workspace, membership } = useWorkspace();
  const [importing, setImporting] = useState(false);

  const resolveClientId = useCallback(
    async (clientName: string): Promise<string | null> => {
      if (!workspace || !clientName.trim()) return null;
      const { data, error } = await supabase
        .from('clients')
        .select('id')
        .eq('workspace_id', workspace.id)
        .ilike('name', clientName.trim())
        .limit(2);
      if (error || !data?.length) return null;
      return data[0].id;
    },
    [workspace]
  );

  const resolveProductAreaId = useCallback(
    async (productAreaName: string): Promise<string | null> => {
      if (!workspace || !productAreaName.trim()) return null;
      const { data, error } = await supabase
        .from('product_structure_items')
        .select('id')
        .eq('workspace_id', workspace.id)
        .ilike('name', productAreaName.trim())
        .limit(2);
      if (error || !data?.length) return null;
      return data[0].id;
    },
    [workspace]
  );

  const handleComplete = useCallback(
    async (result: ImportResult) => {
      if (!workspace || !membership) {
        toast.error('Workspace not loaded');
        return;
      }
      const rawRows = result.rows || [];
      const rows: FeedbackImportRow[] = [];
      for (const raw of rawRows) {
        const parsed = feedbackImportSchema.safeParse(raw);
        if (parsed.success) rows.push(parsed.data);
        else {
          toast.error(`Row validation failed: ${parsed.error.errors[0]?.message || 'Unknown error'}`);
          return;
        }
      }
      if (rows.length === 0) {
        toast.info('No valid rows to import');
        onOpenChange(false);
        return;
      }
      setImporting(true);
      const BATCH_SIZE = 50;
      let successCount = 0;
      const errors: string[] = [];
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const toInsert = await Promise.all(
          batch.map(async (row) => {
            const clientName = getClientNameFromRow(row);
            const client_id = clientName
              ? await resolveClientId(clientName)
              : null;
            const product_item_id = row.product_area?.trim()
              ? await resolveProductAreaId(row.product_area)
              : null;
            return {
              row,
              client_id,
              product_item_id,
            };
          })
        );
        const feedbackInserts = toInsert.map(({ row, client_id }) => ({
          workspace_id: workspace.id,
          client_id: client_id || null,
          contributor: row.contributor || null,
          source: row.source || 'import',
          content: row.content,
          sentiment: row.sentiment || 'neutral',
          product_area_id: null,
          priority: row.priority ?? 3,
          rating: row.rating ?? null,
          status: row.status || 'New',
          created_by: membership.user_id,
        }));
        const { data, error } = await supabase
          .from('feedback')
          .insert(feedbackInserts)
          .select('id');
        if (error) {
          errors.push(error.message);
        } else {
          const ids = data?.map((d) => d.id) || [];
          successCount += ids.length;
          for (let j = 0; j < toInsert.length && j < ids.length; j++) {
            const { product_item_id } = toInsert[j];
            if (product_item_id) {
              await supabase.from('feedback_product_mapping').insert({
                feedback_id: ids[j],
                product_item_id,
                product_id: null,
              });
            }
          }
        }
      }
      setImporting(false);
      if (successCount > 0) {
        toast.success(`Imported ${successCount} feedback item(s)`, {
          description: errors.length
            ? `${errors.length} batch(es) had errors: ${errors[0]}`
            : undefined,
        });
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error('Import failed', {
          description: errors[0] || 'Could not insert feedback',
        });
      }
    },
    [workspace, membership, resolveClientId, resolveProductAreaId, onOpenChange, onSuccess]
  );

  if (!open) return null;

  return (
    <CSVImporter
      columns={feedbackImportColumns}
      modalIsOpen={open}
      modalOnCloseTriggered={() => onOpenChange(false)}
      onComplete={handleComplete}
      isModal
      primaryColor="#245F73"
      customStyles={importCsvTheme}
    />
  );
}
